import { useSyncExternalStore } from "react";
import {
  getFirebaseAuth,
  isAuthAvailable,
  subscribeAuth,
  waitForAuthReady,
} from "./authClient";
import { setUserProperty } from "./telemetry";
import { BILLING_CONFIG } from "./billingConfig";

/**
 * Premium entitlement — the client-side layer that decides whether a paid
 * feature button "just works" or pops the {@link import("../components/PremiumUpsellOverlay").PremiumUpsellOverlay}.
 *
 * The single source of truth is the Firebase Auth custom claim `stripeRole`,
 * set automatically by the official Firebase Stripe extension
 * (`invertase/firestore-stripe-payments`) whenever the user holds an active
 * subscription on a Stripe product whose metadata includes
 * `firebaseRole: "premium"`. The extension owns the webhook and the claim;
 * we just read the claim out of the ID token and turn it into a typed React
 * state.
 *
 * Resolution order (highest priority first):
 *   1. URL query param `?premium=1` / `?premium=0` (dev/test only)
 *   2. localStorage override `roomcanvas:premiumOverride` (Settings UI)
 *   3. `stripeRole === "premium"` custom claim from Firebase Auth
 *
 * The dev override exists so QA can validate the upsell + post-purchase UI
 * without paying for a real Stripe subscription. Cloud Functions ignore
 * the client override and meter against the verified `stripeRole` claim
 * (`hasPremiumRole` in `functions/src/auth.ts`). Against production
 * functions a forced-premium client still hits the free-tier cap and
 * gets `FREE_QUOTA_EXCEEDED`; the AI sheets keep that error inline
 * rather than bouncing to the upsell (which would auto-close). When
 * `VITE_FIREBASE_USE_EMULATORS=true`, {@link setPremiumOverride} also
 * stamps `stripeRole` on the Auth emulator user so local callables
 * honor the toggle.
 */

/** Registry of features unlocked by the single Premium SKU. The values are
 *  copy used in the upsell overlay and the dev-mode Premium override UI;
 *  keep them aligned with the marketing positioning. */
export const PREMIUM_FEATURES = {
  ai_layout_feng_shui: {
    label: "Feng Shui layout",
    headline: "Every room, every move explained",
    blurb:
      `${BILLING_CONFIG.premiumIncludedCredits} credits a month for Feng Shui layouts with the reasoning behind each piece, a score for any room you draw, and real products from any listing URL.`,
    signIn: "Feng Shui layouts and Layout Score need an account.",
  },
  product_url_import: {
    label: "Product URL Import",
    headline: "Every room, every move explained",
    blurb:
      `${BILLING_CONFIG.premiumIncludedCredits} credits a month for Feng Shui layouts with the reasoning behind each piece, a score for any room you draw, and real products from any listing URL.`,
    signIn: "Importing products needs an account.",
  },
  collab_unlimited: {
    label: "Unlimited collaboration",
    headline: "Design every room together",
    blurb: `Free covers ${BILLING_CONFIG.freeSharedRooms} shared room with ${BILLING_CONFIG.freeRoomCollaborators} partner. Premium shares all your rooms with up to ${BILLING_CONFIG.premiumRoomCollaborators} people — plus Feng Shui layouts and product import.`,
    signIn: "Sharing rooms needs an account.",
  },
} as const satisfies Record<string, PremiumFeatureDefinition>;

export interface PremiumFeatureDefinition {
  /** Short, sentence-case label — used in chips and the override toggle. */
  label: string;
  /** Title-cased headline for the upsell overlay. */
  headline: string;
  /** One-sentence value prop shown beneath the headline. */
  blurb: string;
  /** Why a visitor without an account is asked to sign in. Never mentions paying. */
  signIn: string;
}

export type PremiumFeatureKey = keyof typeof PREMIUM_FEATURES;

/** The `firebaseRole` metadata value on the Stripe product. The extension
 *  copies this verbatim into the user's `stripeRole` custom claim. */
export const PREMIUM_ROLE = "premium";

/** The custom-claim key the extension writes. Lifted out so the server-side
 *  `requirePremium` check (functions/src/auth.ts) can import it from one
 *  place and stay in sync. */
export const STRIPE_ROLE_CLAIM = "stripeRole";

const OVERRIDE_STORAGE_KEY = "roomcanvas:premiumOverride";

export type PremiumStatus =
  | "loading"
  | "premium"
  | "not-premium"
  | "signed-out"
  | "unavailable";

export interface PremiumState {
  status: PremiumStatus;
  /** Convenience: `status === "premium"`. */
  isPremium: boolean;
  /**
   * Premium comes from the reverse trial (`users/{uid}/private/trial`),
   * not a subscription: the server honours the Premium allowance as a
   * hard cap and nothing is metered to Stripe. `trialEndsAt` is ms.
   */
  trial: boolean;
  trialEndsAt: number | null;
  /** True iff the current status is the result of a local dev override
   *  rather than a real subscription claim. Surfaced in the override UI
   *  so QA can see "this is the override talking" at a glance. */
  overridden: boolean;
  /** The real `stripeRole === "premium"` claim, ignoring any local
   *  override. The usage meter bills against this so a UI-only override
   *  doesn't pretend the server will honor the Premium included quota. */
  claimIsPremium: boolean;
}

const NO_TRIAL = { trial: false, trialEndsAt: null } as const;
const INITIAL: PremiumState = {
  status: "loading",
  isPremium: false,
  overridden: false,
  claimIsPremium: false,
  ...NO_TRIAL,
};
const SIGNED_OUT: PremiumState = {
  status: "signed-out",
  isPremium: false,
  overridden: false,
  claimIsPremium: false,
  ...NO_TRIAL,
};
const UNAVAILABLE: PremiumState = {
  status: "unavailable",
  isPremium: false,
  overridden: false,
  claimIsPremium: false,
  ...NO_TRIAL,
};
const NOT_PREMIUM: PremiumState = {
  status: "not-premium",
  isPremium: false,
  overridden: false,
  claimIsPremium: false,
  ...NO_TRIAL,
};
const PREMIUM: PremiumState = {
  status: "premium",
  isPremium: true,
  overridden: false,
  claimIsPremium: true,
  ...NO_TRIAL,
};
/** Premium through the reverse trial (no claim, no Stripe). */
const trialState = (endsAt: number): PremiumState => ({
  status: "premium",
  isPremium: true,
  overridden: false,
  claimIsPremium: false,
  trial: true,
  trialEndsAt: endsAt,
});

let underlying: PremiumState = INITIAL;
let publishedState: PremiumState = INITIAL;

let urlOverride: boolean | null = null;
let localOverride: boolean | null = null;
let overridesLoaded = false;

const listeners = new Set<() => void>();
let subscribed = false;
let unsubAuth: (() => void) | null = null;

function loadOverridesOnce(): void {
  if (overridesLoaded) return;
  overridesLoaded = true;
  if (typeof window === "undefined") return;

  try {
    const param = new URLSearchParams(window.location.search).get("premium");
    if (param !== null) {
      const v = param.trim().toLowerCase();
      if (v === "1" || v === "true" || v === "on" || v === "yes")
        urlOverride = true;
      else if (v === "0" || v === "false" || v === "off" || v === "no")
        urlOverride = false;
    }
  } catch {
    // Bad URL — leave urlOverride null.
  }

  try {
    const raw = window.localStorage.getItem(OVERRIDE_STORAGE_KEY);
    if (raw !== null) {
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed === "boolean") localOverride = parsed;
    }
  } catch {
    // Corrupted JSON or storage unavailable — start clean.
  }
}

function notify(): void {
  for (const l of listeners) l();
}

function publish(): void {
  const override = urlOverride ?? localOverride;
  let next: PremiumState;
  if (override === true) {
    next = {
      ...PREMIUM,
      overridden: true,
      claimIsPremium: underlying.claimIsPremium,
    };
  } else if (override === false) {
    next = {
      ...NOT_PREMIUM,
      overridden: true,
      claimIsPremium: underlying.claimIsPremium,
    };
  } else {
    next = underlying;
  }
  if (
    next.status === publishedState.status &&
    next.isPremium === publishedState.isPremium &&
    next.overridden === publishedState.overridden &&
    next.claimIsPremium === publishedState.claimIsPremium &&
    next.trial === publishedState.trial &&
    next.trialEndsAt === publishedState.trialEndsAt
  ) {
    return;
  }
  publishedState = next;
  notify();
}

let stampedIsPremium: boolean | null = null;

function setUnderlying(next: PremiumState): void {
  underlying = next;
  publish();
  // GA4 user properties follow the real claim (never the dev override),
  // so an upgrade mid-session re-segments the user without a reload.
  if (next.status === "premium" || next.status === "not-premium") {
    const isPremium = next.status === "premium";
    if (stampedIsPremium !== isPremium) {
      stampedIsPremium = isPremium;
      setUserProperty("is_premium", isPremium);
      setUserProperty("plan", next.trial ? "trial" : isPremium ? "premium" : "free");
    }
  } else if (next.status === "signed-out") {
    stampedIsPremium = null;
  }
}

// ---------------------------------------------------------------------------
// Reverse trial — `users/{uid}/private/trial`, written by the server
// ---------------------------------------------------------------------------

let claimState: PremiumState = INITIAL;
let trialEndsAt: number | null = null;
let unsubTrial: (() => void) | null = null;
let trialTimer: ReturnType<typeof setTimeout> | null = null;

/** Combine the claim and the trial doc into the underlying state. */
function recompute(): void {
  const active = trialEndsAt != null && trialEndsAt > Date.now();
  if (claimState.status === "not-premium" && active && trialEndsAt != null) {
    setUnderlying(trialState(trialEndsAt));
  } else {
    setUnderlying(claimState);
  }
  // Flip back to free the moment the trial ends, without a reload.
  if (trialTimer) clearTimeout(trialTimer);
  trialTimer = null;
  if (active && trialEndsAt != null) {
    trialTimer = setTimeout(recompute, Math.min(trialEndsAt - Date.now() + 50, 2 ** 31 - 1));
  }
}

function watchTrial(uid: string | null): void {
  if (unsubTrial) {
    unsubTrial();
    unsubTrial = null;
  }
  trialEndsAt = null;
  if (!uid) return;
  void (async () => {
    try {
      const { getFirebaseFirestore } = await import("./firebase");
      const db = getFirebaseFirestore();
      if (!db) return;
      const { doc, onSnapshot } = await import("firebase/firestore");
      unsubTrial = onSnapshot(
        doc(db, "users", uid, "private", "trial"),
        (snap) => {
          const endsAt = snap.exists() ? (snap.data() as { endsAt?: { toMillis?: () => number } }).endsAt : null;
          trialEndsAt = endsAt && typeof endsAt.toMillis === "function" ? endsAt.toMillis() : null;
          if (trialEndsAt != null) rememberTrial(uid);
          recompute();
        },
        () => {
          trialEndsAt = null;
          recompute();
        }
      );
    } catch {
      // Firestore unavailable: the claim alone decides.
    }
  })();
}

/**
 * Start the reverse trial for the signed-in account (idempotent on the
 * server). Called by the auth bridge right after a sign-up / account
 * link; the trial doc listener above unlocks Premium when it lands.
 */
export async function startReverseTrial(): Promise<{ endsAt: number; started: boolean } | null> {
  const auth = getFirebaseAuth();
  if (!auth?.currentUser || auth.currentUser.isAnonymous) return null;
  try {
    const { getFirebaseApp, getFunctionsRegion } = await import("./firebase");
    const app = getFirebaseApp();
    if (!app) return null;
    const { getFunctions, httpsCallable } = await import("firebase/functions");
    const fn = httpsCallable<Record<string, never>, { endsAt: number; started: boolean }>(
      getFunctions(app, getFunctionsRegion()),
      "startReverseTrial"
    );
    const res = await fn({});
    trialEndsAt = res.data.endsAt;
    recompute();
    return res.data;
  } catch {
    return null;
  }
}

const TRIAL_SEEN_KEY = "room-canvas:trial:seen";
/** Remembered per uid once a trial doc was seen, so an ended trial reads
 *  "your week ended" instead of "never had Premium" without another read. */
export function hadTrial(uid: string | undefined): boolean {
  if (!uid || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(`${TRIAL_SEEN_KEY}:${uid}`) === "1";
  } catch {
    return false;
  }
}
function rememberTrial(uid: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${TRIAL_SEEN_KEY}:${uid}`, "1");
  } catch {
    /* ignore */
  }
}

/** Whole days left in the trial (0 when ending today), or null outside one. */
export function trialDaysLeft(state: PremiumState, now = Date.now()): number | null {
  if (!state.trial || state.trialEndsAt == null) return null;
  return Math.max(0, Math.ceil((state.trialEndsAt - now) / (24 * 3600 * 1000)));
}

async function readRoleFromCurrentToken(
  forceRefresh: boolean
): Promise<PremiumState> {
  const auth = getFirebaseAuth();
  if (!auth || !auth.currentUser) return SIGNED_OUT;
  try {
    const result = await auth.currentUser.getIdTokenResult(forceRefresh);
    const role = (result.claims as Record<string, unknown>)[STRIPE_ROLE_CLAIM];
    return role === PREMIUM_ROLE ? PREMIUM : NOT_PREMIUM;
  } catch {
    // Token fetch failed (offline, revoked, etc.). Treat as not-premium so
    // the UI errs on the side of showing the upsell, not silently unlocking.
    return NOT_PREMIUM;
  }
}

function ensureSubscribed(): void {
  if (subscribed) return;
  subscribed = true;
  loadOverridesOnce();

  if (!isAuthAvailable()) {
    setUnderlying(UNAVAILABLE);
    return;
  }

  // Apply any URL / localStorage override to publishedState immediately so
  // the first synchronous read sees it — without this, `?premium=1`
  // wouldn't take effect until `subscribeAuth` fired on the next tick.
  publish();

  unsubAuth = subscribeAuth((user) => {
    if (!user) {
      claimState = SIGNED_OUT;
      watchTrial(null);
      recompute();
      return;
    }
    // Anonymous sessions never hold a trial; accounts do.
    watchTrial(user.isAnonymous ? null : user.uid);
    // Don't force-refresh on every auth event — the token we already have
    // is valid for an hour and the role rarely changes. `refreshPremium()`
    // is the explicit force path (post-checkout return).
    void readRoleFromCurrentToken(false).then((state) => {
      claimState = state;
      recompute();
      const override = urlOverride ?? localOverride;
      if (override !== null) queueEmulatorClaimSync(override);
    });
  });
}

function subscribe(listener: () => void): () => void {
  ensureSubscribed();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): PremiumState {
  return publishedState;
}

function getServerSnapshot(): PremiumState {
  return INITIAL;
}

/** React hook — subscribes to the premium entitlement and re-renders on
 *  changes. Safe to call before Firebase resolves; starts in `"loading"`. */
export function usePremium(): PremiumState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Convenience hook for gating a specific feature. Today every key maps to
 *  the same Premium bundle; signature is left feature-keyed so future SKU
 *  splits (e.g. an annual-only feature) don't need a caller change. */
export function usePremiumFeature(_key: PremiumFeatureKey): PremiumState {
  return usePremium();
}

/** Sync read for non-React callers (e.g. the click handler bridge that
 *  decides whether to open the feature sheet or the upsell overlay). */
export function getPremiumState(): PremiumState {
  ensureSubscribed();
  return publishedState;
}

/** Non-React subscription to entitlement changes. Returns an unsubscribe. */
export function subscribePremium(listener: () => void): () => void {
  return subscribe(listener);
}

/**
 * Force-refresh the user's Firebase Auth ID token and re-read the role
 * claim. Call this after returning from Stripe Checkout (the extension
 * may have just stamped `stripeRole: "premium"` onto the user, but the
 * cached token won't see the change for up to an hour without a force
 * refresh).
 *
 * Awaits Firebase's initial auth-state restoration first. Without that
 * wait, a refresh fired right after a Stripe Checkout redirect would
 * see `auth.currentUser === null` (auth restoration is async — the
 * persisted user lives in IndexedDB) and incorrectly resolve to
 * `signed-out`. {@link pollPremiumUntilActive} interprets `signed-out`
 * as a terminal state and bails out before ever forcing a refresh,
 * which is the root cause of "I just paid and the upsell still pops".
 */
export async function refreshPremium(): Promise<PremiumState> {
  ensureSubscribed();
  if (!isAuthAvailable()) return UNAVAILABLE;
  await waitForAuthReady();
  const next = await readRoleFromCurrentToken(true);
  claimState = next;
  recompute();
  return publishedState;
}

/** Default backoff schedule for {@link pollPremiumUntilActive}, in
 *  milliseconds between attempts. Front-loaded because the extension
 *  webhook usually finishes within ~3–10s, but we keep the tail going to
 *  ~45s total to catch cold starts. The first attempt fires immediately
 *  (no delay) before this schedule kicks in. */
const POLL_DELAYS_MS: readonly number[] = [
  500, 1000, 1500, 2500, 4000, 5000, 7500, 10000, 13000,
];

export interface PollPremiumOptions {
  /** Stop polling early when this signal aborts (e.g. component unmount,
   *  user signed out). */
  signal?: AbortSignal;
  /** Override the backoff schedule. Mostly for tests — production should
   *  stick with the default. The first attempt always fires immediately;
   *  these values are the delays *between* subsequent attempts. */
  delaysMs?: readonly number[];
}

/**
 * Poll {@link refreshPremium} with exponential-ish backoff until the
 * user becomes premium or the schedule runs out. Returns the final
 * {@link PremiumState}.
 *
 * Why this exists: Stripe Checkout redirects the browser back to the app
 * concurrently with Stripe POSTing `checkout.session.completed` to the
 * `invertase/firestore-stripe-payments` extension. The redirect almost
 * always wins the race — so a one-shot `refreshPremium()` on return sees
 * a token without the `stripeRole` claim and the user gets the upsell
 * modal again the moment they click a paid feature. This helper keeps
 * retrying until the extension's webhook finishes and stamps the claim,
 * typically within a few seconds.
 *
 * Stop conditions:
 *   - The published state becomes `"premium"`.
 *   - The user signs out or auth becomes unavailable (terminal states).
 *   - The schedule runs out (returns the last observed not-premium state).
 *   - `opts.signal` aborts.
 */
export async function pollPremiumUntilActive(
  opts: PollPremiumOptions = {}
): Promise<PremiumState> {
  ensureSubscribed();
  if (!isAuthAvailable()) return UNAVAILABLE;

  const { signal } = opts;
  const delays = opts.delaysMs ?? POLL_DELAYS_MS;

  let state = await refreshPremium();
  if (state.status === "premium") return state;
  // Terminal non-premium states — no point polling. The user will need
  // to sign in (signed-out) or fix their env (unavailable) first.
  if (state.status === "signed-out" || state.status === "unavailable") {
    return state;
  }

  for (const delay of delays) {
    if (signal?.aborted) return state;
    const slept = await sleep(delay, signal);
    if (!slept) return state;
    state = await refreshPremium();
    if (state.status === "premium") return state;
    if (state.status === "signed-out" || state.status === "unavailable") {
      return state;
    }
  }
  return state;
}

/** Promise-based delay that resolves to `false` if the signal aborts
 *  before the timer elapses. Returns `true` on a clean elapse. Exported
 *  only for unit tests; the production path uses `pollPremiumUntilActive`
 *  directly. */
function sleep(ms: number, signal?: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve(true);
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      resolve(false);
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** Stamp / clear `stripeRole` on the Auth emulator user so local Cloud
 *  Functions see the same tier as the UI toggle. No-ops when emulators
 *  are off, nobody is signed in, or the Admin RPC fails — the client
 *  override still applies either way. */
function queueEmulatorClaimSync(premium: boolean): void {
  void import("./emulatorPremiumClaim")
    .then(({ stampEmulatorPremiumClaim }) => stampEmulatorPremiumClaim(premium))
    .then((stamped) => {
      if (stamped) return refreshPremium();
    })
    .catch(() => {
      // Emulator unreachable or module graph still settling in tests.
      // The UI override remains in effect.
    });
}

/** Set the dev override. `null` clears it. Persists to localStorage and
 *  immediately publishes the effective state to subscribers. Against
 *  the Auth emulator, `true`/`false` also stamp or clear `stripeRole`
 *  so AI callables honor the toggle; `null` only drops the UI override
 *  and leaves whatever claim the token currently carries. */
export function setPremiumOverride(value: boolean | null): void {
  loadOverridesOnce();
  localOverride = value;
  if (typeof window !== "undefined") {
    try {
      if (value === null) {
        window.localStorage.removeItem(OVERRIDE_STORAGE_KEY);
      } else {
        window.localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(value));
      }
    } catch {
      // Storage unavailable — in-memory override still applies for this
      // session, just won't survive a reload.
    }
  }
  publish();
  if (value !== null) queueEmulatorClaimSync(value);
}

export function clearPremiumOverride(): void {
  setPremiumOverride(null);
}

export function getPremiumOverride(): boolean | null {
  loadOverridesOnce();
  return localOverride;
}

/** Test-only: wipe subscription state so each test starts clean. */
export function _resetPremiumForTests(): void {
  if (unsubAuth) unsubAuth();
  unsubAuth = null;
  subscribed = false;
  overridesLoaded = false;
  urlOverride = null;
  localOverride = null;
  underlying = INITIAL;
  claimState = INITIAL;
  trialEndsAt = null;
  if (unsubTrial) unsubTrial();
  unsubTrial = null;
  if (trialTimer) clearTimeout(trialTimer);
  trialTimer = null;
  publishedState = INITIAL;
  stampedIsPremium = null;
  listeners.clear();
}
